<template>
  <form class="sf-header-search" :class="{'sf-search-desktop': legacy && !mobile, 'sf-search-mobile': legacy && mobile}"
    role="search" @submit.prevent="submit" @focusout="leaveFocus">
    <div class="sf-search-field">
      <input :id="searchId" ref="input" v-model="query" type="search" maxlength="100"
        placeholder="Search cities or communities" aria-label="Search cities or communities"
        role="combobox" aria-autocomplete="list" aria-haspopup="listbox" autocomplete="off"
        :aria-expanded="String(open)" :aria-controls="searchId + '-results'"
        :aria-activedescendant="active >= 0 ? searchId + '-option-' + active : null"
        @focus="focus" @keydown="keydown">
      <button type="submit" aria-label="Search cities or communities">
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="10" cy="10" r="6.5" stroke="currentColor" stroke-width="1.8" />
          <path d="m15 15 5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
    </div>
    <div v-if="open" class="sf-search-panel">
      <p role="status" aria-live="polite" class="sf-search-status">{{ status }}</p>
      <div :id="searchId + '-results'" role="listbox" aria-label="Cities and communities" :aria-busy="String(loading)">
        <div v-for="group in groups" :key="group.label" role="group" :aria-labelledby="searchId + '-' + group.label">
          <p :id="searchId + '-' + group.label" class="sf-search-heading">{{ group.label }}</p>
          <a v-for="option in group.options" :id="searchId + '-option-' + option.index" :key="option.index"
            :href="option.url" role="option" tabindex="-1" :aria-selected="String(active === option.index)"
            class="sf-search-option" :class="{'sf-search-active': active === option.index}"
            @mousedown.prevent @click.prevent="navigate(option.url)">
            <strong>{{ option.label }}</strong><span v-if="option.location">{{ option.location }}</span>
          </a>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import 'whatwg-fetch'

export default {
  name: 'TopSearch',
  props: {mobile: Boolean, legacy: Boolean},
  data () {
    return {query: '', cities: [], communities: [], open: false, loading: false,
      error: false, active: -1, timer: null, requestId: 0, controller: null,
      searchId: 'sf-search-' + Math.random().toString(36).slice(2)}
  },
  computed: {
    groups () {
      let index = 0
      return [
        {label: 'Cities', options: this.cities.map(item => ({...item, label: item.label, index: index++}))},
        {label: 'Communities', options: this.communities.map(item => ({...item,
          label: item.name, location: [item.city, item.state].filter(Boolean).join(', '), index: index++}))}
      ].filter(group => group.options.length)
    },
    options () { return this.groups.flatMap(group => group.options) },
    status () {
      if (this.query.trim().length < 2) return 'Type at least 2 characters to search.'
      if (this.loading) return 'Searching…'
      if (this.error) return 'Search is unavailable. Press Enter to browse discovery results.'
      if (!this.options.length) return 'No matching cities or communities.'
      return this.options.length + ' suggestions. Use the arrow keys to choose.'
    }
  },
  watch: {
    query () { this.open = true; this.search() }
  },
  mounted () { document.addEventListener('click', this.outsideClick) },
  beforeDestroy () {
    this.cancel()
    document.removeEventListener('click', this.outsideClick)
  },
  methods: {
    cancel () {
      this.requestId++
      clearTimeout(this.timer)
      this.timer = null
      if (this.controller) this.controller.abort()
      this.controller = null
      this.loading = false
    },
    search () {
      this.cancel()
      this.active = -1
      this.cities = []; this.communities = []; this.error = false
      const query = this.query.trim()
      if (query.length < 2) return
      const id = this.requestId
      this.loading = true
      this.timer = setTimeout(() => this.load(query, id), 250)
    },
    async load (query, id) {
      if (id !== this.requestId) return
      this.controller = window.AbortController ? new window.AbortController() : null
      try {
        const response = await fetch('/header-search/?' + new URLSearchParams({q: query}), {
          credentials: 'same-origin', headers: {Accept: 'application/json'},
          ...(this.controller ? {signal: this.controller.signal} : {})
        })
        if (!response.ok) throw new Error('Search unavailable')
        const data = await response.json()
        if (id !== this.requestId) return
        this.cities = data.cities
        this.communities = data.communities
      } catch (error) {
        if (id === this.requestId) this.error = true
      } finally {
        if (id === this.requestId) { this.loading = false; this.controller = null }
      }
    },
    focus () { this.open = true; this.search() },
    close () { this.open = false; this.active = -1; this.cancel() },
    outsideClick (event) { if (!this.$el.contains(event.target)) this.close() },
    leaveFocus (event) { if (!this.$el.contains(event.relatedTarget)) this.close() },
    keydown (event) {
      if (event.isComposing) return
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return }
      if (event.key === 'Enter') { event.preventDefault(); this.submit(); return }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        if (!this.open) { this.focus(); return }
        const count = this.options.length
        if (!count) return
        this.active = this.active < 0
          ? (event.key === 'ArrowDown' ? 0 : count - 1)
          : (this.active + (event.key === 'ArrowDown' ? 1 : -1) + count) % count
      } else if (this.open && this.options.length && ['Home', 'End'].includes(event.key)) {
        event.preventDefault()
        this.active = event.key === 'Home' ? 0 : this.options.length - 1
      } else return
      this.$nextTick(() => {
        const option = document.getElementById(this.searchId + '-option-' + this.active)
        if (option) option.scrollIntoView({block: 'nearest'})
      })
    },
    submit () {
      const selected = this.open && this.options[this.active]
      if (selected) return this.navigate(selected.url)
      if (this.query.trim()) this.navigate('/discover/?' + new URLSearchParams({q: this.query.trim()}) + '#discover')
    },
    navigate (url) {
      // Full-page navigation preserves the existing editors' beforeunload guards.
      window.location.assign(url)
    }
  }
}
</script>

<style scoped>
.sf-header-search { position: relative; flex: 1 1 260px; width: 100%; min-width: 0; max-width: 380px; margin: 0; font: 14px Inter, 'Open Sans', sans-serif; }
.sf-search-field { display: flex; align-items: center; border: 1px solid #77736b; border-radius: 9px; background: #30302e; }
.sf-header-search .sf-search-field input { width: 100%; min-width: 0; padding: 11px 12px; border: 0; border-radius: 9px; outline: none; background: transparent; color: #faf8f2; font: inherit; }
.sf-search-field input::placeholder { color: #ddd9cf; opacity: 1; }
.sf-search-field:focus-within { outline: 2px solid #ffd400; outline-offset: 2px; }
.sf-header-search .sf-search-field button { display: grid; place-items: center; flex-shrink: 0; padding: 10px; border: 0; border-radius: 8px; background: transparent; color: #faf8f2; cursor: pointer; }
.sf-search-panel { position: absolute; z-index: 60; top: calc(100% + 8px); left: 0; width: 100%; min-width: min(300px, calc(100vw - 32px)); max-height: 65vh; overflow-y: auto; background: #fffdf7; color: #222; border: 1px solid #dedbd1; border-radius: 10px; box-shadow: 0 12px 30px #0003; text-align: left; }
.sf-header-search .sf-search-status { margin: 0; padding: 12px; font-size: 12px; color: #625f57; line-height: 1.5; }
.sf-header-search .sf-search-heading { margin: 0; padding: 8px 12px; background: #f0ece2; color: #625f57; font-size: 12px; font-weight: 600; }
.sf-header-search .sf-search-option { display: block; padding: 12px; color: #222 !important; text-decoration: none; font-size: 14px; line-height: 1.5; overflow-wrap: anywhere; }
.sf-search-option strong, .sf-search-option span { display: block; }
.sf-search-option span { color: #625f57; font-size: 12px; }
.sf-search-option:hover, .sf-search-active { background: #fff6cc; }
.sf-header-search button:focus-visible { outline: 2px solid #ffd400; outline-offset: 1px; }
@media (max-width: 991px) { .sf-search-desktop { display: none; } }
@media (min-width: 992px) { .sf-search-mobile { display: none; } }
@media (max-width: 600px) { .sf-header-search { order: 3; flex-basis: 100%; max-width: none; } }
</style>
